<template>
  <div class="w-full flex p-2 items-center text-gray-900 dark:text-gray-50">
    <div class="w-full">
      <div class="flex items-center justify-between">
        <div>
          <h5 class="text-gray-900 font-medium dark:text-gray-50">
            {{ customerForm.form.title }}
          </h5>
          <p class="text-sm font-normal">
            {{ customerForm.createdLabel }}
          </p>
          <form-status :customerForm="customerForm" class="mt-2" />
        </div>

        <div class="flex items-center space-x-1">
          <o-tooltip v-if="!isFormStateLocked" :text="$t('form.fillInForm')">
            <o-panel-link
              tag="o-button"
              flat
              size="sm"
              :path="`/customer/form/${customerForm.idCustomerForm}`"
              icon="edit"
            >
              {{ $t('general.edit') }}
            </o-panel-link>
          </o-tooltip>

          <o-tooltip v-if="!isRequestStateRequested && !isRequestStateScheduled" :text="$t('form.view')">
            <o-panel-link
              tag="o-button"
              flat
              size="sm"
              icon="view"
              :path="`/customer/form/view?id=${customerForm.idCustomerForm}`"
            >
              {{ $t('general.view') }}
            </o-panel-link>
          </o-tooltip>

          <o-tooltip v-if="isFormStateLocked" :text="$t('components.customer.profile.customerForm.reopen')">
            <o-button flat size="sm" icon="unlock" @click="handleFormReopen()" />
          </o-tooltip>



          <o-tooltip v-if="isRequestStateNone || isRequestStateCompleted || isRequestStateUpdateRequested" :text="$t('general.email')">
            <o-button flat size="sm" icon="email" @click="handleFormEmail()" />
          </o-tooltip>

          <o-tooltip v-if="!isFormStateLocked" :text="$t('components.customer.profile.customerForm.requestUpdate')">
            <o-button flat size="sm" icon="email" @click="handleFormRequestUpdate()" />
          </o-tooltip>

          <o-tooltip v-if="customerForm.reservationCustomer" :text="$t('general.appointment')">
            <o-panel-link
              tag="o-button"
              size="sm"
              flat
              :path="`/appointment/attendee/${customerForm.reservationCustomer.idReservationCustomer}`"
              icon="reservation"
            />
          </o-tooltip>

          <o-tooltip v-if="customerForm.reservation" :text="$t('general.appointment')">
            <o-panel-link
              v-if="customerForm.reservation"
              tag="o-button"
              size="sm"
              flat
              :path="`/appointment/${customerForm.reservation.idReservation}?date=${customerForm.reservation.date}`"
              icon="reservation"
            />
          </o-tooltip>

          <o-history-link
            :item="customerForm"
            icon="history"
            size="sm"
            slim
            flat
            model="CustomerForm"
          />

          <o-tooltip :text="$t('general.delete')">
            <o-button
              variant="danger"
              flat
              size="sm"
              icon="delete"

              @click="handleFormDelete()"
            />
          </o-tooltip>
        </div>
      </div>
    </div>

    <reopen-action
      ref="reopenAction"
      :customerForm="customerForm"
      :customer="customer"
      @success="onFormReopenSuccess()"
    />

    <request-update-action
      ref="requestUpdateAction"
      :customerForm="customerForm"
      :customer="customer"
      @success="onFormRequestUpdateSuccess()"
    />

    <delete-action
      ref="deleteAction"
      :customerForm="customerForm"
      :customer="customer"
      @success="onFormDeleteSuccess()"
    />

    <email-action
      ref="emailAction"
      :customerForm="customerForm"
      :customer="customer"
    />
  </div>
</template>

<script setup lang="ts">
import FormController from '@/components/customerForm/edit/FormController.vue'
import FormStatus from '@/components/customerForm/elements/Status.vue'
import SignaturePreview from '@/components/customerForm/elements/SignaturePreview.vue'
import ReopenAction from '@/components/customerForm/actions/Reopen.vue'
import RequestUpdateAction from '@/components/customerForm/actions/RequestUpdate.vue'
import DeleteAction from '@/components/customerForm/actions/Delete.vue'
import EmailAction from '@/components/customerForm/actions/Email.vue'

import {
  ODescriptionDetails,
} from '@/components/elements'

interface Props {
  customerForm: Record<string, any>
  customer: Record<string, any>
}

const props = defineProps<Props>()

const { $bus } = useNuxtApp()

const deleteAction = ref<any>()
const reopenAction = ref<any>()
const requestUpdateAction = ref<any>()
const emailAction = ref<any>()

const isRequestStateNone = computed(() => props.customerForm.isRequestStateNone)
const isRequestStateRequested = computed(() => props.customerForm.isRequestStateRequested)
const isRequestStateCompleted = computed(() => props.customerForm.isRequestStateCompleted)
const isRequestStateScheduled = computed(() => props.customerForm.isRequestStateScheduled)
const isRequestStateUpdateRequested = computed(() => props.customerForm.isRequestStateUpdateRequested)
const isFormStateLocked = computed(() => props.customerForm.isFormStateLocked)
const isFormStateReopened = computed(() => props.customerForm.isFormStateReopened)

const handleFormDelete = () => {
  deleteAction.value.open()
}

const handleFormReopen = () => {
  reopenAction.value.open()
}

const handleFormRequestUpdate = () => {
  requestUpdateAction.value.open()
}

const handleFormEmail = () => {
  emailAction.value.open()
}

const onFormDeleteSuccess = () => {
  $bus.emit.deleted('CustomerForm', props.customerForm)
}

const onFormReopenSuccess = () => {
  $bus.emit.updated('CustomerForm', props.customerForm)
}

const onFormRequestUpdateSuccess = () => {
  $bus.emit.updated('CustomerForm', props.customerForm)
}
</script>
